import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  amazonConnectApi,
  SYNC_DOMAINS,
  type ActivityEntry,
  type ApplicationConfig,
  type ConnectionNotification,
  type ConnectionSummary,
  type DashboardSummary,
  type ErrorRecord,
  type MarketplaceHealth,
  type ConnectionHealthSummary,
  type CatalogProduct,
  type CatalogProductPage,
  type SellerConnection,
  type SPAPILogEntry,
  type SyncDashboard,
  type SyncJobRecord,
  type CommerceSyncHealth,
} from '../../api/amazonconnect';
import { WorkspaceSelector } from '../../components/WorkspaceSelector';
import { defaultOAuthRedirectUri, persistOAuthSession } from './AmazonOAuthCallbackPage';
import { getWorkspaceId } from '../../lib/auth-storage';

const DEFAULT_REDIRECT = defaultOAuthRedirectUri();

type WizardStep = 'request' | 'authorize' | 'polling' | 'done';

export function ConnectDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // ALPHA-000001 / CERT F-01: workspace must be chosen on Connect (no silent default).
  const [workspaceId, setWorkspaceId] = useState(() => getWorkspaceId() ?? '');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all tenant connections (no workspace filter — selected workspace binds on create).
      const [dash, conns] = await Promise.all([
        amazonConnectApi.dashboard(),
        amazonConnectApi.listConnections(),
      ]);
      setDashboard(dash);
      setConnections(conns);
      // Prefer connected/approved so a leftover OAuth stub (pending) never wins selection.
      const preferred =
        conns.find((c) => c.status === 'approved' || c.status === 'authorized') ??
        conns[0];
      const current = selectedId ? conns.find((c) => c.id === selectedId) : undefined;
      const currentIsIncomplete =
        !current || current.status === 'pending' || current.status === 'authorizing' || current.status === 'failed';
      if (preferred && (currentIsIncomplete || !selectedId)) {
        const preferredIsLive = preferred.status === 'approved' || preferred.status === 'authorized';
        if (preferredIsLive || !selectedId || !current) {
          setSelectedId(preferred.id);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll while any connection is mid-OAuth so Authorizing never appears frozen.
  useEffect(() => {
    const needsPoll = connections.some((c) => c.status === 'authorizing' || c.status === 'pending');
    if (!needsPoll) return;
    const id = window.setInterval(() => {
      void refresh();
    }, 4000);
    return () => window.clearInterval(id);
  }, [connections, refresh]);

  const selected = connections.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Amazon Seller Connection</h1>
          <p className="mt-1 text-sm text-slate-600">
            Connect Seller Central accounts, manage marketplaces, synchronize data, and monitor health
          </p>
        </div>
        <div className="flex min-w-[14rem] flex-col gap-2 sm:min-w-[18rem]">
          <WorkspaceSelector
            currentId={workspaceId || undefined}
            onSwitch={(w) => {
              setWorkspaceId(w.id);
              void refresh();
            }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>
          <Link
            to="/amazon/connect/settings"
            className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-800 hover:bg-brand-100"
          >
            Edit Configuration
          </Link>
        </div>
      </header>

      {!workspaceId && (
        <div
          role="status"
          className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-800"
        >
          <p>
            Select a workspace above before configuring, authorizing, or synchronizing Amazon.
            Scope is not assumed.
          </p>
          <p className="mt-1 text-slate-600">
            No workspaces in the list?{' '}
            <Link to="/workspaces/new" className="font-medium text-brand-700 underline">
              Create your first workspace
            </Link>{' '}
            — Amazon authorization needs one before it can start.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {dashboard && <SummaryCards dashboard={dashboard} />}

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-4 lg:col-span-1">
          <OAuthWizard workspaceId={workspaceId} onComplete={refresh} />
          <ConnectionList
            connections={connections}
            summaries={dashboard?.connections}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={loading}
          />
        </section>

        <section className="space-y-4 lg:col-span-2">
          {selected ? (
            <ConnectionDetail connection={selected} onRefresh={refresh} />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
              {workspaceId
                ? 'Select a connection or start the OAuth wizard to connect a seller account.'
                : 'Select a workspace to manage connections.'}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SummaryCards({ dashboard }: { dashboard: DashboardSummary }) {
  const cards = [
    { label: 'Total', value: dashboard.total_connections, tone: 'text-slate-900' },
    { label: 'Approved', value: dashboard.approved_connections, tone: 'text-emerald-700' },
    { label: 'Pending', value: dashboard.pending_connections, tone: 'text-amber-700' },
    { label: 'Active Syncs', value: dashboard.active_sync_jobs, tone: 'text-brand-700' },
    { label: 'Open Errors', value: dashboard.open_errors, tone: 'text-red-700' },
    { label: 'Notifications', value: dashboard.unread_notifications, tone: 'text-violet-700' },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
      {cards.map((c) => (
        <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-400">{c.label}</p>
          <p className={`mt-1 text-2xl font-bold ${c.tone}`}>{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function ConnectionList({
  connections,
  summaries,
  selectedId,
  onSelect,
  loading,
}: {
  connections: SellerConnection[];
  summaries?: ConnectionSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="font-semibold text-slate-900">Connections</h2>
      </div>
      <ul className="divide-y divide-slate-100">
        {loading && connections.length === 0 && (
          <li className="px-4 py-6 text-sm text-slate-500">Loading…</li>
        )}
        {!loading && connections.length === 0 && (
          <li className="px-4 py-6 text-sm text-slate-500">No connections yet.</li>
        )}
        {connections.map((c) => {
          const summary = summaries?.find((s) => s.id === c.id);
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                className={`w-full px-4 py-3 text-left transition hover:bg-slate-50 ${
                  selectedId === c.id ? 'bg-brand-50 border-l-4 border-brand-500' : ''
                }`}
              >
                <p className="font-medium text-slate-900">{c.seller_ref}</p>
                <p className="text-xs text-slate-500">
                  <StatusBadge status={c.status} /> · {c.marketplace_ids?.length ?? 0} marketplaces
                  {summary?.token_expiring && ' · token expiring'}
                </p>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ConnectionDetail({ connection, onRefresh }: { connection: SellerConnection; onRefresh: () => void }) {
  const [tab, setTab] = useState<'overview' | 'sync' | 'activity' | 'spapi' | 'errors' | 'admin'>('overview');
  const [marketplaces, setMarketplaces] = useState<MarketplaceHealth[]>([]);
  const [syncDash, setSyncDash] = useState<SyncDashboard | null>(null);
  const [jobs, setJobs] = useState<SyncJobRecord[]>([]);
  const [commerceHealth, setCommerceHealth] = useState<CommerceSyncHealth | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [spapiLog, setSpapiLog] = useState<SPAPILogEntry[]>([]);
  const [errors, setErrors] = useState<ErrorRecord[]>([]);
  const [notifications, setNotifications] = useState<ConnectionNotification[]>([]);
  const [health, setHealth] = useState<ConnectionHealthSummary | null>(null);
  const [liveStatus, setLiveStatus] = useState(connection.status);
  const [authResult, setAuthResult] = useState<string | null>(null);
  const [refreshTokenInput, setRefreshTokenInput] = useState('');
  const [sellingPartnerInput, setSellingPartnerInput] = useState('');
  const [flexSiteCodesInput, setFlexSiteCodesInput] = useState('');
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identityResult, setIdentityResult] = useState<string | null>(null);
  const [editingIdentity, setEditingIdentity] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [catalogMeta, setCatalogMeta] = useState<CatalogProductPage | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLiveStatus(connection.status);
  }, [connection.status]);

  const loadDetail = useCallback(async () => {
    try {
      const [mp, sync, jobList, act, errs, notifs, healthSummary, live, log, products, commerce] = await Promise.all([
        amazonConnectApi.connectionMarketplaceHealth(connection.id),
        amazonConnectApi.syncDashboard(connection.id),
        amazonConnectApi.listSyncJobs(connection.id),
        amazonConnectApi.listActivity(connection.id),
        amazonConnectApi.listErrors(connection.id, true),
        amazonConnectApi.listNotifications(connection.id, true),
        amazonConnectApi.connectionHealth(connection.id).catch(() => null),
        amazonConnectApi.getConnection(connection.id).catch(() => null),
        // Communication log needs the durable store; tolerate its absence.
        amazonConnectApi.listSPAPILog(connection.id, 50).catch(() => [] as SPAPILogEntry[]),
        amazonConnectApi.listCatalogProductsPage(connection.id, { page: 1, page_size: 50 }).catch(
          () =>
            ({
              products: [],
              count: 0,
              total_count: 0,
              page: 1,
              page_size: 50,
              pages: 0,
              remaining: 0,
              has_more: false,
            }) as CatalogProductPage,
        ),
        amazonConnectApi.commerceSyncHealth(connection.id).catch(() => null),
      ]);
      setMarketplaces(mp);
      setSyncDash(sync);
      setJobs(jobList);
      setActivity(act);
      setSpapiLog(log);
      setCatalogProducts(products.products);
      setCatalogMeta(products);
      setErrors(errs);
      setNotifications(notifs);
      setCommerceHealth(commerce);
      if (healthSummary) setHealth(healthSummary);
      if (live) {
        setLiveStatus(live.status);
        if (
          live.status !== connection.status &&
          (live.status === 'approved' || live.status === 'authorized' || live.status === 'failed')
        ) {
          onRefresh();
        }
      }
    } catch {
      /* API may be unavailable in dev */
    }
  }, [connection.id, connection.status, onRefresh]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // COMMERCE-SYNC-FOUNDATION-000001 — Dashboard open enqueues realtime Orders refresh
  useEffect(() => {
    if (!connection.id || connection.status !== 'approved') return;
    void amazonConnectApi.commerceRealtimeRefresh(connection.id, ['orders']).catch(() => undefined);
  }, [connection.id, connection.status]);

  useEffect(() => {
    if (liveStatus !== 'authorizing' && liveStatus !== 'pending') return;
    const id = window.setInterval(() => {
      void loadDetail();
    }, 3000);
    return () => window.clearInterval(id);
  }, [liveStatus, loadDetail]);

  const runAuthTest = async () => {
    setBusy(true);
    try {
      const r = await amazonConnectApi.authTest(connection.id);
      setAuthResult(r.success ? 'Authentication OK' : r.message ?? 'Auth test failed');
    } catch (e) {
      setAuthResult(e instanceof Error ? e.message : 'Auth test failed');
    } finally {
      setBusy(false);
    }
  };

  const triggerDomainSync = async (domain: string) => {
    setBusy(true);
    try {
      await amazonConnectApi.triggerSync(connection.id, domain);
      await loadDetail();
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const triggerCommerceProfile = async (profileKey: string, msDomain?: string) => {
    setBusy(true);
    try {
      if (msDomain) {
        await amazonConnectApi.triggerSync(connection.id, msDomain);
      } else {
        await amazonConnectApi.commerceRealtimeRefresh(connection.id, [profileKey]);
      }
      await loadDetail();
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const triggerFullSync = async () => {
    setBusy(true);
    try {
      await amazonConnectApi.triggerFullSync(connection.id);
      await loadDetail();
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await amazonConnectApi.approveConnection(connection.id);
      onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await amazonConnectApi.disconnectConnection(connection.id);
      onRefresh();
    } catch (e) {
      setAuthResult(e instanceof Error ? e.message : 'Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const reconnect = async () => {
    setBusy(true);
    try {
      await amazonConnectApi.reconnectConnection(connection.id);
      onRefresh();
    } catch (e) {
      setAuthResult(e instanceof Error ? e.message : 'Reconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const regionForConnection = () => {
    const mp = connection.marketplace_ids?.[0];
    // India + EU marketplaces use SP-API EU region; host is selected server-side by marketplace.
    if (mp === 'ATVPDKIKX0DER' || mp === 'A2EUQ1WTGCTBG2' || mp === 'A1AM78C64UM0Y8' || mp === 'A2Q3Y263D00KWC') {
      return 'NA';
    }
    if (mp === 'A1VC38T7YXB528' || mp === 'A19VAU5U5O7RUS' || mp === 'A39IBJ37TRP1C6') {
      return 'FE';
    }
    return 'EU';
  };

  const restartAuth = async () => {
    setBusy(true);
    try {
      const init = await amazonConnectApi.restartAuthorization(connection.id, DEFAULT_REDIRECT, regionForConnection());
      persistOAuthSession({
        connection_id: connection.id,
        redirect_uri: DEFAULT_REDIRECT,
      });
      // Same-tab navigation preserves OAuth session; avoids authorizing forever after MD1000/browser cancel.
      window.location.assign(init.authorization_url);
    } catch (e) {
      setAuthResult(e instanceof Error ? e.message : 'Restart authorization failed');
    } finally {
      setBusy(false);
    }
  };

  // Self Authorization: for SP-API apps registered for your own seller account,
  // Amazon shows no Login/Redirect URI fields and the OAuth consent flow fails with
  // MD9100. Amazon issues a refresh token directly instead; the backend validates it
  // against LWA before storing, so a bad paste is rejected rather than saved.
  const importRefreshToken = async () => {
    const token = refreshTokenInput.trim();
    const spid = sellingPartnerInput.trim();
    if (!token) {
      setImportResult('Paste the refresh token from Seller Central first.');
      return;
    }
    if (!token.startsWith('Atzr|')) {
      setImportResult('That does not look like an LWA refresh token — it must start with "Atzr|".');
      return;
    }
    if (!spid) {
      setImportResult('Selling Partner ID / Merchant Token is required for Product Catalog (Seller Central → Account Info).');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      await amazonConnectApi.importRefreshToken(connection.id, token, spid);
      setRefreshTokenInput('');
      setImportResult('Connected. Amazon accepted the refresh token and seller identity was stored.');
      await loadDetail();
      onRefresh();
    } catch (e) {
      setImportResult(e instanceof Error ? e.message : 'Amazon rejected the refresh token');
    } finally {
      setImporting(false);
    }
  };

  const saveSellerIdentity = async () => {
    const spid = sellingPartnerInput.trim();
    if (!spid) {
      setIdentityResult(
        'Enter Merchant Token (Seller ID) from Seller Central → Settings → Account Info. Example: A28XEFQ6SVEK2Z — not amzn1.application… or amzn1.sp.solution…',
      );
      return;
    }
    setIdentitySaving(true);
    setIdentityResult(null);
    try {
      await amazonConnectApi.setSellerIdentity(connection.id, {
        selling_partner_id: spid,
        // empty array allows clear; backend only mutates when slice is non-nil or csv non-empty
        seller_flex_site_codes: flexSiteCodesInput
          .split(/[,\s]+/)
          .map((c) => c.trim())
          .filter(Boolean),
      });
      setIdentityResult('Seller identity saved. You can run Catalog Sync.');
      setEditingIdentity(false);
      await loadDetail();
    } catch (e) {
      setIdentityResult(e instanceof Error ? e.message : 'Failed to save seller identity');
    } finally {
      setIdentitySaving(false);
    }
  };

  const openEditIdentity = () => {
    const current = health?.seller_id || health?.merchant_id || '';
    setSellingPartnerInput(current && !current.startsWith('seller-') ? current : '');
    setFlexSiteCodesInput((health?.seller_flex_site_codes ?? []).join(', '));
    setIdentityResult(null);
    setEditingIdentity(true);
  };

  const deleteConn = async () => {
    if (!window.confirm('Delete this connection? This cannot be undone.')) return;
    setBusy(true);
    try {
      await amazonConnectApi.deleteConnection(connection.id);
      onRefresh();
    } catch (e) {
      setAuthResult(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { id: 'overview' as const, label: 'Overview' },
    { id: 'sync' as const, label: 'Synchronization' },
    { id: 'activity' as const, label: 'Activity' },
    { id: 'spapi' as const, label: 'SP-API Log' },
    { id: 'errors' as const, label: 'Errors' },
    { id: 'admin' as const, label: 'Admin' },
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{connection.seller_ref}</h2>
            <p className="text-sm text-slate-500">
              <StatusBadge status={liveStatus} /> · Workspace {connection.workspace_id}
            </p>
            {connection.last_error && (
              <p className="mt-1 text-xs text-red-600">{connection.last_error}</p>
            )}
            <ConnectionLifecycleBar status={liveStatus} />
          </div>
          <div className="flex flex-wrap gap-2">
            {(liveStatus === 'authorized') && (
              <button
                type="button"
                disabled={busy}
                onClick={approve}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                Approve
              </button>
            )}
            {(liveStatus === 'authorizing' || liveStatus === 'failed') && (
              <button
                type="button"
                disabled={busy}
                onClick={restartAuth}
                className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
              >
                Restart Authorization
              </button>
            )}
            {liveStatus === 'failed' && (
              <button
                type="button"
                disabled={busy}
                onClick={restartAuth}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                Retry Authorization
              </button>
            )}
            {(liveStatus === 'approved' || liveStatus === 'authorized') && (
              <button
                type="button"
                disabled={busy}
                onClick={disconnect}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Disconnect
              </button>
            )}
            {(liveStatus === 'disconnected' || liveStatus === 'failed' || liveStatus === 'rejected') && (
              <button
                type="button"
                disabled={busy}
                onClick={reconnect}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Reconnect
              </button>
            )}
            {['pending', 'failed', 'authorizing', 'disconnected', 'rejected'].includes(liveStatus) && (
              <button
                type="button"
                disabled={busy}
                onClick={deleteConn}
                className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Delete
              </button>
            )}
            <Link
              to="/amazon/connect/settings"
              className="rounded-lg border border-brand-200 px-3 py-1.5 text-sm font-medium text-brand-800 hover:bg-brand-50"
            >
              Edit Configuration
            </Link>
            <button
              type="button"
              disabled={busy}
              onClick={() => setTab('activity')}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              View Logs
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={runAuthTest}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Auth Test
            </button>
          </div>
        </div>
        {authResult && <p className="mt-2 text-sm text-slate-600">{authResult}</p>}
        {notifications.length > 0 && (
          <div className="mt-3 space-y-1">
            {notifications.slice(0, 3).map((n) => (
              <p key={n.id} className="text-xs text-violet-700">
                {n.title}: {n.body}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-100 px-4">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium ${
              tab === t.id ? 'border-b-2 border-brand-500 text-brand-700' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === 'overview' && (
          <div className="space-y-4">
            <ConnectionHealthPanel
              health={health}
              status={liveStatus}
              onEditSellerId={
                health?.connected || liveStatus === 'approved' || liveStatus === 'authorized'
                  ? openEditIdentity
                  : undefined
              }
            />

            {(health?.connected || liveStatus === 'approved' || liveStatus === 'authorized') &&
              health &&
              (!health.identity_complete || editingIdentity) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <h3 className="text-sm font-semibold text-amber-900">
                  {health.identity_complete ? 'Edit Seller ID' : 'Seller Identity required'}
                </h3>
                <p className="mt-1 text-xs text-amber-800">
                  Product Catalog needs your <strong>Merchant Token / Selling Partner ID</strong> from Seller
                  Central → Settings → Account Info. It looks like <span className="font-mono">A28XEFQ6SVEK2Z</span>
                  .
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  Do <strong>not</strong> paste LWA Client ID (
                  <span className="font-mono">amzn1.application-oa2-client…</span>) or Application ID (
                  <span className="font-mono">amzn1.sp.solution…</span>) — those are app credentials, not Seller ID.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <input
                    value={sellingPartnerInput}
                    onChange={(e) => setSellingPartnerInput(e.target.value)}
                    placeholder="A28XEFQ6SVEK2Z"
                    className="min-w-[14rem] flex-1 rounded-lg border border-amber-300 px-3 py-2 font-mono text-xs"
                    aria-label="Amazon Merchant Token / Selling Partner ID"
                  />
                  <input
                    value={flexSiteCodesInput}
                    onChange={(e) => setFlexSiteCodesInput(e.target.value)}
                    placeholder="Seller Flex site codes (comma-separated, optional)"
                    className="min-w-[14rem] flex-1 rounded-lg border border-amber-300 px-3 py-2 font-mono text-xs"
                    aria-label="Seller Flex site codes"
                  />
                  <button
                    type="button"
                    disabled={identitySaving}
                    onClick={saveSellerIdentity}
                    className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
                  >
                    {identitySaving ? 'Saving…' : 'Save Seller ID'}
                  </button>
                  {health.identity_complete && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingIdentity(false);
                        setIdentityResult(null);
                      }}
                      className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm text-amber-900"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {identityResult && (
                  <p className={`mt-2 text-xs ${identityResult.startsWith('Seller identity') ? 'text-emerald-700' : 'text-red-700'}`}>
                    {identityResult}
                  </p>
                )}
              </div>
            )}

            {liveStatus !== 'approved' && liveStatus !== 'authorized' && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                <h3 className="text-sm font-semibold text-indigo-900">Self Authorization (no OAuth needed)</h3>
                <p className="mt-1 text-xs text-indigo-800">
                  Use this when your SP-API app is registered for your own seller account. Those apps have no
                  Login URI / Redirect URI fields in Seller Central, so the consent flow fails with{' '}
                  <span className="font-mono">MD9100</span>. Amazon gives you a refresh token directly instead.
                </p>
                <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-xs text-indigo-800">
                  <li>Seller Central → Apps &amp; Services → Develop Apps</li>
                  <li>On your app row open the <strong>LWA credentials / Authorize</strong> menu</li>
                  <li>Choose <strong>Authorize</strong> (self-authorization) and generate the refresh token</li>
                  <li>Copy Selling Partner ID / Merchant Token from Account Info</li>
                  <li>Paste both below — refresh token starts with <span className="font-mono">Atzr|</span></li>
                </ol>
                <div className="mt-3 flex flex-col gap-2">
                  <input
                    value={sellingPartnerInput}
                    onChange={(e) => setSellingPartnerInput(e.target.value)}
                    placeholder="Selling Partner ID (A1…)"
                    className="w-full rounded-lg border border-indigo-300 px-3 py-2 font-mono text-xs"
                    aria-label="Amazon Selling Partner ID"
                  />
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="password"
                      autoComplete="off"
                      spellCheck={false}
                      value={refreshTokenInput}
                      onChange={(e) => setRefreshTokenInput(e.target.value)}
                      placeholder="Atzr|IwEBI..."
                      className="min-w-[18rem] flex-1 rounded-lg border border-indigo-300 px-3 py-2 font-mono text-xs"
                      aria-label="Amazon LWA refresh token"
                    />
                    <button
                      type="button"
                      disabled={importing}
                      onClick={importRefreshToken}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {importing ? 'Validating with Amazon…' : 'Connect with token'}
                    </button>
                  </div>
                </div>
                {importResult && (
                  <p
                    className={`mt-2 text-xs ${
                      importResult.startsWith('Connected') ? 'text-emerald-700' : 'text-red-700'
                    }`}
                  >
                    {importResult}
                  </p>
                )}
                <p className="mt-2 text-[11px] text-indigo-700">
                  The token is validated against Amazon before it is stored, encrypted at rest, and never returned
                  by the API.
                </p>
              </div>
            )}

            <MarketplaceSelector connectionId={connection.id} marketplaces={marketplaces} onUpdate={loadDetail} />
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-slate-500">Credential</dt>
                <dd className="font-mono text-xs">{connection.credential_id ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Last Sync</dt>
                <dd>{connection.last_sync_at ? new Date(connection.last_sync_at).toLocaleString() : '—'}</dd>
              </div>
            </dl>
          </div>
        )}

        {tab === 'sync' && syncDash && (
          <div className="space-y-4">
            <SyncHealthPanel
              health={commerceHealth}
              busy={busy}
              onRefreshProfile={triggerCommerceProfile}
              onReload={() => void loadDetail()}
            />
            <SyncPanel
              syncDash={syncDash}
              jobs={jobs}
              busy={busy}
              onDomainSync={triggerDomainSync}
              onFullSync={triggerFullSync}
            />
            <CatalogProductsPanel products={catalogProducts} meta={catalogMeta} onRefresh={loadDetail} />
          </div>
        )}

        {tab === 'activity' && <ActivityTimeline entries={activity} />}
        {tab === 'spapi' && <SPAPICommunicationLog entries={spapiLog} onRefresh={loadDetail} />}
        {tab === 'errors' && (
          <ErrorCenter errors={errors} connectionId={connection.id} onRetry={loadDetail} />
        )}
        {tab === 'admin' && <AdminPanel connectionId={connection.id} />}
      </div>
    </div>
  );
}

function OAuthWizard({
  workspaceId,
  onComplete,
}: {
  workspaceId: string;
  onComplete: () => void;
}) {
  const [step, setStep] = useState<WizardStep>('request');
  const [sellerRef, setSellerRef] = useState('');
  const [marketplaceIds, setMarketplaceIds] = useState<string[]>(['A21TJRUUN4KGV']);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [registry, setRegistry] = useState<MarketplaceHealth[]>([]);
  const [applications, setApplications] = useState<ApplicationConfig[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingApps, setLoadingApps] = useState(true);
  const [authPhase, setAuthPhase] = useState<string>('Connecting…');

  const activeApps = applications.filter((a) => a.status !== 'disabled');
  const selectedApp = activeApps[0] ?? null;
  const defaultMarketplace =
    selectedApp?.marketplace_id ??
    selectedApp?.metadata?.marketplace_id ??
    selectedApp?.metadata?.marketplaceId ??
    'A21TJRUUN4KGV';
  const defaultRegion = selectedApp?.region ?? selectedApp?.metadata?.region ?? 'EU';

  useEffect(() => {
    amazonConnectApi.listMarketplaces().then(setRegistry).catch(() => setRegistry([]));
  }, []);

  useEffect(() => {
    setLoadingApps(true);
    amazonConnectApi
      .listApplications()
      .then((apps) => {
        const active = apps.filter((a) => a.status !== 'disabled');
        setApplications(active);
        if (active.length > 0) {
          const mp = active[0].marketplace_id ?? active[0].metadata?.marketplace_id ?? active[0].metadata?.marketplaceId;
          if (mp) setMarketplaceIds([mp]);
        }
      })
      .catch(() => setApplications([]))
      .finally(() => setLoadingApps(false));
  }, []);

  const startConnection = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const apps = applications.length ? applications : (await amazonConnectApi.listApplications()).filter((a) => a.status !== 'disabled');
      if (apps.length === 0) {
        setMessage('Configuration required — configure your Amazon application in Settings first.');
        return;
      }
      if (!apps[0].has_client_secret) {
        setMessage('Amazon application is missing a client secret. Configure it before starting OAuth.');
        return;
      }
      const scopeWorkspaceId = workspaceId || getWorkspaceId() || '';
      if (!scopeWorkspaceId) {
        setMessage(
          'Select a workspace above before connecting Amazon (identity chain requires X-Workspace-Id). ' +
            'If the workspace list is empty, create one first at Platform → Workspaces → New.',
        );
        return;
      }
      const conn = await amazonConnectApi.requestConnection({
        workspace_id: scopeWorkspaceId,
        seller_ref: sellerRef || `seller-${Date.now()}`,
        application_config_id: apps[0].id,
        marketplace_ids: marketplaceIds,
      });
      setConnectionId(conn.id);
      setStep('authorize');
      const init = await amazonConnectApi.initiateAuth(conn.id, DEFAULT_REDIRECT, defaultRegion);
      setAuthUrl(init.authorization_url);
      persistOAuthSession({
        connection_id: conn.id,
        redirect_uri: DEFAULT_REDIRECT,
      });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to start connection');
    } finally {
      setBusy(false);
    }
  };

  const goToAmazon = () => {
    if (!authUrl || !connectionId) return;
    setAuthPhase('Opening Amazon Seller Central…');
    // Same-tab navigation guarantees Amazon's redirect returns to this origin
    // with spapi_oauth_code. Popups/new tabs often leave the board stuck on Waiting for Callback.
    persistOAuthSession({
      connection_id: connectionId,
      redirect_uri: DEFAULT_REDIRECT,
    });
    window.location.assign(authUrl);
  };

  useEffect(() => {
    if (step !== 'polling' || !connectionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const conn = await amazonConnectApi.getConnection(connectionId);
        if (cancelled) return;
        if (conn.status === 'approved' || conn.status === 'authorized') {
          setAuthPhase('Connected');
          setStep('done');
          onComplete();
          return;
        }
        if (conn.status === 'failed') {
          setAuthPhase('Failed');
          setMessage(conn.last_error ?? 'Authorization failed');
          setStep('authorize');
          return;
        }
        if (conn.status === 'authorizing') {
          setAuthPhase('Authorizing on Amazon…');
        }
      } catch {
        /* keep polling */
      }
    };
    void poll();
    const id = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [step, connectionId, onComplete]);

  if (loadingApps) {
    return (
      <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4 shadow-sm">
        <h2 className="font-semibold text-slate-900">OAuth Wizard</h2>
        <p className="mt-2 text-sm text-slate-500">Loading Amazon application…</p>
      </div>
    );
  }

  if (!selectedApp) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 shadow-sm">
        <h2 className="font-semibold text-slate-900">OAuth Wizard</h2>
        <p className="mt-2 text-sm text-amber-900">Configuration required — no active Amazon application found.</p>
        <Link
          to="/amazon/connect/settings"
          className="mt-3 inline-block rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Go to Settings → Amazon
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-4 shadow-sm">
      <h2 className="font-semibold text-slate-900">OAuth Wizard</h2>
      <p className="mt-1 text-xs text-slate-600">PKCE-protected authorization flow</p>

      <dl className="mt-3 space-y-1 rounded-lg border border-brand-100 bg-white/70 px-3 py-2 text-xs">
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Application</dt>
          <dd className="font-medium text-slate-900">{selectedApp.name}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Marketplace</dt>
          <dd className="font-medium text-slate-900">{defaultMarketplace}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Region</dt>
          <dd className="font-medium text-slate-900">{defaultRegion}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Client ID</dt>
          <dd className="truncate font-mono text-slate-700">{selectedApp.client_id.slice(0, 18)}…</dd>
        </div>
      </dl>

      {step === 'request' && (
        <div className="mt-3 space-y-3">
          <input
            type="text"
            placeholder="Seller reference"
            value={sellerRef}
            onChange={(e) => setSellerRef(e.target.value)}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {registry.slice(0, 5).map((m) => (
              <label key={m.marketplace_id} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={marketplaceIds.includes(m.marketplace_id)}
                  onChange={(e) => {
                    setMarketplaceIds((prev) =>
                      e.target.checked
                        ? [...prev, m.marketplace_id]
                        : prev.filter((id) => id !== m.marketplace_id),
                    );
                  }}
                />
                {m.country ?? m.marketplace_id.slice(0, 4)}
              </label>
            ))}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={startConnection}
            className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Start Connection
          </button>
        </div>
      )}

      {step === 'authorize' && authUrl && (
        <div className="mt-3 space-y-3">
          <button
            type="button"
            onClick={goToAmazon}
            className="block w-full rounded-lg bg-orange-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-orange-700"
          >
            Authorize on Amazon
          </button>
          <p className="break-all text-[11px] font-mono text-slate-600">{authUrl}</p>
          {!authUrl.includes('version=beta') && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              This URL has no version=beta. Amazon returns MD1000 if the SP-API application is still Draft.
              Set Publication status to Draft in Amazon Application Settings, then restart authorization.
            </p>
          )}
          <p className="text-xs text-slate-500">
            Redirect URI: <span className="font-mono">{DEFAULT_REDIRECT}</span>
          </p>
          <p className="text-xs text-slate-500">Connection ID: {connectionId}</p>
        </div>
      )}

      {step === 'polling' && (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-slate-700">{authPhase}</p>
          <p className="text-xs text-slate-500">
            Complete authorization in the Amazon tab, then return here. Status updates automatically.
          </p>
        </div>
      )}

      {message && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-red-600">{message}</p>
          {message.includes('configured') && (
            <Link
              to="/amazon/connect/settings"
              className="inline-block text-xs font-medium text-brand-700 underline"
            >
              Configure Amazon Application
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function MarketplaceSelector({
  connectionId,
  marketplaces,
  onUpdate,
}: {
  connectionId: string;
  marketplaces: MarketplaceHealth[];
  onUpdate: () => void;
}) {
  const [registry, setRegistry] = useState<MarketplaceHealth[]>([]);
  useEffect(() => {
    amazonConnectApi.listMarketplaces().then(setRegistry).catch(() => setRegistry([]));
  }, []);

  const toggle = async (marketplaceId: string, enabled: boolean) => {
    await amazonConnectApi.associateMarketplace(connectionId, marketplaceId, enabled);
    onUpdate();
  };

  const linked = new Set(marketplaces.map((m) => m.marketplace_id));

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900">Marketplace Selection</h3>
      <ul className="mt-2 space-y-2">
        {registry.map((m) => {
          const linkedMp = marketplaces.find((x) => x.marketplace_id === m.marketplace_id);
          const enabled = linkedMp?.enabled ?? false;
          return (
            <li
              key={m.marketplace_id}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm"
            >
              <span>
                {m.name} ({m.region})
              </span>
              <button
                type="button"
                onClick={() => toggle(m.marketplace_id, !enabled)}
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {enabled ? 'Enabled' : linked.has(m.marketplace_id) ? 'Disabled' : 'Add'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SyncHealthPanel({
  health,
  busy,
  onRefreshProfile,
  onReload,
}: {
  health: CommerceSyncHealth | null;
  busy: boolean;
  onRefreshProfile: (profileKey: string, msDomain?: string) => void;
  onReload: () => void;
}) {
  if (!health) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Sync Health will appear after the connection is readable. Open this tab on an approved connection.
      </div>
    );
  }
  const tierLabel = (t: number) => (t === 1 ? 'T1 · 10m' : t === 2 ? 'T2 · hourly' : 'T3 · daily');
  const fmtDay = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return '—';
    }
  };
  return (
    <section className="space-y-3" aria-label="Commerce Sync Health">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Sync Health</h3>
          <p className="mt-0.5 text-xs text-slate-500">{health.note}</p>
          {(health.backfill_horizon || health.rate_budget_pct) && (
            <p className="mt-1 text-[11px] text-slate-500">
              Backfill horizon {health.backfill_horizon ?? '—'} · every {health.backfill_interval ?? '2h'} · ≤
              {health.rate_budget_pct ?? 70}% SP-API budget
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onReload}
          className="cursor-pointer rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-50"
        >
          Reload health
        </button>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        <span>Total {health.counts.total ?? 0}</span>
        <span>Runnable {health.counts.runnable ?? 0}</span>
        <span>Warning {health.counts.warning ?? 0}</span>
        <span>Failed {health.counts.failed ?? 0}</span>
        <span>Schedule-only {health.counts.scheduled_only ?? 0}</span>
        <span>Horizon done {health.counts.horizon_done ?? 0}</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-2 py-2 font-medium">Domain</th>
              <th className="px-2 py-2 font-medium">Tier</th>
              <th className="px-2 py-2 font-medium">Data from</th>
              <th className="px-2 py-2 font-medium">Data to</th>
              <th className="px-2 py-2 font-medium">Last Sync</th>
              <th className="px-2 py-2 font-medium">Next Sync</th>
              <th className="px-2 py-2 font-medium">Status</th>
              <th className="px-2 py-2 font-medium">Records</th>
              <th className="px-2 py-2 font-medium">Quality</th>
              <th className="px-2 py-2 font-medium">Complete</th>
              <th className="px-2 py-2 font-medium">Refresh</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {health.domains.map((d) => (
              <tr key={d.key} className="bg-white">
                <td className="px-2 py-2">
                  <div className="font-medium text-slate-900">{d.label}</div>
                  <div className="font-mono text-[10px] text-slate-500">
                    {d.key}
                    {d.pull_type ? ` · ${d.pull_type}` : ''}
                    {d.horizon_reached ? ' · horizon' : ''}
                  </div>
                </td>
                <td className="px-2 py-2 whitespace-nowrap">
                  {tierLabel(d.tier)}
                  <div className="text-[10px] text-slate-500">{d.frequency}</div>
                </td>
                <td className="px-2 py-2 whitespace-nowrap">{fmtDay(d.data_from)}</td>
                <td className="px-2 py-2 whitespace-nowrap">{fmtDay(d.data_to)}</td>
                <td className="px-2 py-2 whitespace-nowrap">
                  {d.last_sync_at ? new Date(d.last_sync_at).toLocaleString() : '—'}
                </td>
                <td className="px-2 py-2 whitespace-nowrap">
                  {d.next_sync_at ? new Date(d.next_sync_at).toLocaleString() : '—'}
                </td>
                <td className="px-2 py-2 capitalize">{d.status}</td>
                <td className="px-2 py-2 font-mono">
                  {d.records_received}/{d.records_persisted}
                  {d.records_displayed != null ? `/${d.records_displayed}` : ''}
                </td>
                <td className="px-2 py-2" title={d.quality_detail}>
                  <span
                    className={
                      d.quality === 'PASS'
                        ? 'text-emerald-700'
                        : d.quality === 'WARNING'
                          ? 'text-amber-700'
                          : 'text-slate-500'
                    }
                  >
                    {d.quality}
                  </span>
                </td>
                <td className="px-2 py-2" title={d.completeness_detail}>
                  <span
                    className={
                      d.completeness === 'PASS'
                        ? 'text-emerald-700'
                        : d.completeness === 'WARNING' || d.completeness === 'FAIL'
                          ? 'text-amber-700'
                          : 'text-slate-500'
                    }
                  >
                    {d.completeness ?? '—'}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <button
                    type="button"
                    disabled={busy || !d.runnable}
                    onClick={() => onRefreshProfile(d.key, d.marketsync_domain)}
                    className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
                    title={d.runnable ? 'Manual refresh' : 'Schedule-only until MarketSync domain is wired'}
                  >
                    Refresh
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {health.recent_logs && health.recent_logs.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <h4 className="text-xs font-semibold text-slate-800">Recent sync log</h4>
          <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto font-mono text-[10px] text-slate-600">
            {health.recent_logs.map((e) => (
              <li key={e.id}>
                {new Date(e.created_at).toLocaleString()} · {e.domain} · {e.reason}/{e.pull_type} ·{' '}
                {fmtDay(e.window_from)}→{fmtDay(e.window_to)} · {e.records_received}/{e.records_persisted}
                {e.completeness ? ` · complete=${e.completeness}` : ''}
                {e.error ? ` · err=${e.error}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function SyncPanel({
  syncDash,
  jobs,
  busy,
  onDomainSync,
  onFullSync,
}: {
  syncDash: SyncDashboard;
  jobs: SyncJobRecord[];
  busy: boolean;
  onDomainSync: (domain: string) => void;
  onFullSync: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 text-sm">
        <span>Active: {syncDash.active_jobs}</span>
        <span>Queued: {syncDash.queued_jobs}</span>
        <span>Failed: {syncDash.failed_jobs}</span>
        <span>Completed: {syncDash.completed_jobs}</span>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onFullSync}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
      >
        Full Sync (All Domains)
      </button>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {SYNC_DOMAINS.map((domain: string) => (
          <button
            key={domain}
            type="button"
            disabled={busy}
            onClick={() => onDomainSync(domain)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-left text-sm capitalize hover:bg-slate-50 disabled:opacity-50"
          >
            {domain} sync
          </button>
        ))}
      </div>
      {jobs.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Recent Jobs</h4>
          <ul className="mt-2 divide-y divide-slate-100 text-sm">
            {jobs.slice(0, 8).map((j) => (
              <li key={j.id} className="flex justify-between py-2">
                <span className="capitalize">
                  {j.domain} ({j.mode})
                </span>
                <StatusBadge status={j.state} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ConnectionLifecycleBar({ status }: { status: string }) {
  const steps = [
    { id: 'pending', label: 'Pending' },
    { id: 'authorizing', label: 'Authorizing' },
    { id: 'callback', label: 'Waiting for Callback' },
    { id: 'token', label: 'Exchanging Token' },
    { id: 'verify', label: 'Verifying Seller' },
    { id: 'connected', label: 'Connected' },
  ];
  const activeIndex =
    status === 'failed'
      ? -1
      : status === 'approved' || status === 'authorized'
        ? 5
        : status === 'authorizing'
          ? 2
          : status === 'pending'
            ? 0
            : 1;
  if (status === 'failed') {
    return (
      <div className="mt-2 space-y-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
        <p>Authorization failed — use Restart Authorization or Delete.</p>
        <p>
          <strong>MD9100</strong> — your app on Amazon has no Login URI / Redirect URI. Fix in Seller Central →
          Develop Apps → Edit App: set Login URI to <span className="font-mono">/amazon/connect</span> and Redirect
          URI to <span className="font-mono">/amazon/connect/callback</span> on this domain, then save. Nothing in
          this platform can fix it — the fields live on Amazon.
        </p>
        <p>
          <strong>MD1000</strong> — a Draft app used the production OAuth workflow. Set Application Publication
          Status to Draft (adds version=beta) and restart.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-1 text-[10px] uppercase tracking-wide text-slate-500">
        {steps.map((s, i) => (
          <span
            key={s.id}
            className={
              i === activeIndex
                ? 'font-semibold text-brand-700'
                : i < activeIndex
                  ? 'font-medium text-slate-500'
                  : 'text-slate-400'
            }
          >
            {s.label}
            {i < steps.length - 1 ? ' →' : ''}
          </span>
        ))}
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full bg-brand-600 transition-all duration-500"
          style={{ width: `${Math.max(8, ((activeIndex + 1) / steps.length) * 100)}%` }}
        />
      </div>
    </div>
  );
}

function ConnectionHealthPanel({
  health,
  status,
  onEditSellerId,
}: {
  health: ConnectionHealthSummary | null;
  status: string;
  onEditSellerId?: () => void;
}) {
  if (!health) {
    return (
      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        Connection Health — loading…
      </div>
    );
  }
  const sellerLabel = health.seller_name || 'not set';
  const sellerId = health.seller_id || health.merchant_id || 'not set';
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">Connection Health / Seller Identity</h3>
        {onEditSellerId && (
          <button
            type="button"
            onClick={onEditSellerId}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Edit Seller ID
          </button>
        )}
      </div>
      <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-slate-500">Seller Name</dt>
          <dd>{sellerLabel}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Seller ID (Merchant Token)</dt>
          <dd className="font-mono text-xs">{sellerId}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Marketplace</dt>
          <dd>{health.marketplace_name ?? health.marketplace_id ?? 'not set'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Country</dt>
          <dd>{health.country_code ?? 'not set'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Connection Date</dt>
          <dd>
            {health.connected_at
              ? new Date(health.connected_at).toLocaleString()
              : health.connection_age ?? 'not set'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Token Expiry</dt>
          <dd>{health.token_expires_at ? new Date(health.token_expires_at).toLocaleString() : 'not set'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Last Successful Sync</dt>
          <dd>{health.last_sync_at ? new Date(health.last_sync_at).toLocaleString() : 'not set'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Seller Flex Site Codes</dt>
          <dd className="font-mono text-xs">
            {(health.seller_flex_site_codes ?? []).length
              ? health.seller_flex_site_codes!.join(', ')
              : 'not set'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Identity Complete</dt>
          <dd className={health.identity_complete ? 'text-emerald-700' : 'text-amber-700'}>
            {health.identity_complete ? 'yes' : 'no — catalog blocked'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Health</dt>
          <dd className="capitalize">{health.health}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Status</dt>
          <dd className="capitalize">{health.connected ? 'connected' : status}</dd>
        </div>
      </dl>
    </div>
  );
}

function CatalogProductsPanel({
  products,
  meta,
  onRefresh,
}: {
  products: CatalogProduct[];
  meta: CatalogProductPage | null;
  onRefresh: () => void;
}) {
  const total = meta?.total_count ?? products.length;
  const remaining = meta?.remaining ?? 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Product Catalog (PostgreSQL)</h3>
          <p className="text-xs text-slate-500">
            Source of truth: marketsync_canonical_objects · showing {products.length}
            {meta ? ` of ${total} (page ${meta.page}/${meta.pages || 1}, remaining after page: ${remaining})` : ''}
          </p>
          <p className="mt-0.5 text-xs text-brand-700">
            Full search / sort / all pages:{' '}
            <Link to="/amazon/catalog" className="underline">
              Product Catalog page
            </Link>
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>
      {products.length === 0 ? (
        <p className="mt-3 text-sm text-slate-600">
          No products yet. Set Seller Identity, run Catalog full sync, then refresh. Count must match Seller
          Central.
        </p>
      ) : (
        <div className="mt-3 max-h-96 overflow-auto">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-50 text-slate-500">
              <tr>
                <th className="px-2 py-1.5 font-medium">SKU</th>
                <th className="px-2 py-1.5 font-medium">ASIN</th>
                <th className="px-2 py-1.5 font-medium">Title</th>
                <th className="px-2 py-1.5 font-medium">Brand</th>
                <th className="px-2 py-1.5 font-medium">Type</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={`${p.sku}-${p.asin ?? ''}`} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-mono">{p.sku}</td>
                  <td className="px-2 py-1.5 font-mono">{p.asin ?? '—'}</td>
                  <td className="max-w-xs truncate px-2 py-1.5">{p.title ?? '—'}</td>
                  <td className="px-2 py-1.5">{p.brand ?? '—'}</td>
                  <td className="px-2 py-1.5">{p.product_type ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Amazon SP-API communication log. Every row is one real HTTP exchange with
 * Amazon, captured verbatim by the adapter. Access tokens are stripped at capture
 * time, so nothing secret reaches this view.
 */
function SPAPICommunicationLog({ entries, onRefresh }: { entries: SPAPILogEntry[]; onRefresh: () => void }) {
  const [open, setOpen] = useState<string | null>(null);

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
        <p className="font-medium text-slate-900">No Amazon calls recorded yet.</p>
        <p className="mt-1">
          Entries appear after a sync runs against a connected seller. Each row holds the operation,
          HTTP status, Amazon request id and the exact response body.
        </p>
        <button
          type="button"
          onClick={onRefresh}
          className="mt-3 rounded-lg border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="font-medium text-slate-900">SP-API Communication Log</h3>
          <p className="text-xs text-slate-500">{entries.length} most recent Amazon responses</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">Operation</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Amazon Request ID</th>
              <th className="px-4 py-2">Size</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.map((e) => (
              <Fragment key={e.id}>
                <tr>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-600">
                    {new Date(e.received_at).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{e.operation}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        e.status_code >= 400
                          ? 'bg-red-100 text-red-800'
                          : e.status_code >= 300
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {e.status_code}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{e.amazon_request_id ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">{e.body_bytes.toLocaleString('en-IN')} B</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setOpen(open === e.id ? null : e.id)}
                      className="text-xs font-medium text-brand-700 hover:underline"
                    >
                      {open === e.id ? 'Hide' : 'Body'}
                    </button>
                  </td>
                </tr>
                {open === e.id && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50 px-4 py-3">
                      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-900 p-3 text-xs text-slate-100">
                        {e.body_preview || '(empty body)'}
                      </pre>
                      {e.body_preview && e.body_bytes > e.body_preview.length && (
                        <p className="mt-1 text-xs text-slate-500">
                          Truncated for display — {e.body_bytes.toLocaleString('en-IN')} bytes stored.
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityTimeline({ entries }: { entries: ActivityEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">No activity yet.</p>;
  }
  return (
    <ul className="space-y-3">
      {entries.map((e) => (
        <li key={e.id} className="border-l-2 border-brand-200 pl-3">
          <p className="text-sm font-medium text-slate-900">{e.message}</p>
          <p className="text-xs text-slate-500">
            {e.category} · {e.action} · {new Date(e.created_at).toLocaleString()}
          </p>
        </li>
      ))}
    </ul>
  );
}

function ErrorCenter({
  errors,
  connectionId,
  onRetry,
}: {
  errors: ErrorRecord[];
  connectionId: string;
  onRetry: () => void;
}) {
  if (errors.length === 0) {
    return <p className="text-sm text-slate-500">No open errors.</p>;
  }
  return (
    <ul className="space-y-2">
      {errors.map((e) => (
        <li key={e.id} className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm">
          <p className="font-medium text-red-900">{e.code}</p>
          <p className="text-red-700">{e.message}</p>
          {e.retryable && (
            <button
              type="button"
              onClick={() => amazonConnectApi.retryError(connectionId, e.id).then(onRetry)}
              className="mt-1 text-xs font-medium text-red-800 underline"
            >
              Retry
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function AdminPanel({ connectionId }: { connectionId: string }) {
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);
  const [admin, setAdmin] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    amazonConnectApi.diagnostics(connectionId).then(setHealth).catch(() => setHealth(null));
    amazonConnectApi.adminHealth().then(setAdmin).catch(() => setAdmin(null));
  }, [connectionId]);

  return (
    <div className="space-y-4 text-sm">
      <div>
        <h4 className="font-semibold text-slate-900">Diagnostics</h4>
        <pre className="mt-1 overflow-auto rounded bg-slate-50 p-2 text-xs">
          {JSON.stringify(health, null, 2)}
        </pre>
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">Platform Health</h4>
        <pre className="mt-1 overflow-auto rounded bg-slate-50 p-2 text-xs">
          {JSON.stringify(admin, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    approved: 'bg-emerald-100 text-emerald-800',
    authorized: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    authorizing: 'bg-amber-100 text-amber-800',
    rejected: 'bg-red-100 text-red-800',
    disconnected: 'bg-slate-100 text-slate-600',
    completed: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-red-100 text-red-800',
    queued: 'bg-violet-100 text-violet-800',
    running: 'bg-blue-100 text-blue-800',
  };
  // Show "Approved" for live Amazon tokens — "connected" confused operators looking for approval state.
  const label =
    status === 'approved' ? 'Approved' : status === 'authorized' ? 'Authorized' : status;
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium capitalize ${
        colors[status] ?? 'bg-slate-100 text-slate-700'
      }`}
    >
      {label}
    </span>
  );
}
